// import components
import { IconButton, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { WeekValueCell } from 'components/shared/RosterViewDataGrid/components/cells/WeekValueCell';
import BacklogCategoryLabel from '../BacklogCategoryLabel';
import OutsideTreatmentAppointmentCard from '../OutsideTreatmentAppointmentCard';

// import external libraries
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

// import utils
import {
  getCategoryRowKey,
  getWeekValueMagnitude,
  selectWeekEvents
} from '../BacklogGrid/BacklogGrid.utils';
import {
  formatEntityDisplayLabel,
  getIconType
} from 'components/mrp/mrp.utils';

// import styles
import {
  Breadcrumb,
  CareReceiverBand,
  Container,
  DayCell,
  DayHeader,
  LabelCell,
  Row
} from './BacklogWeekDetail.styles';
import { CareReceiverEntityIcon } from '../BacklogGrid/BacklogGrid.styles';

// import types
import type { BacklogWeekDetailProps } from './BacklogWeekDetail.types';
import type { ClientResponse } from 'generated/index';
import type { WeekValue } from 'components/shared/RosterViewDataGrid';

const asWeekValue = (value: unknown): WeekValue | null =>
  value && typeof value === 'object' && 'current' in value && 'total' in value
    ? (value as WeekValue)
    : null;

/**
 * The per-day view of one week, opened from an outside-treatment cell. Per care receiver it shows the
 * appointments outside treatment as cards in their day column, and the week's tekort/overschot the
 * same way the weeks grid does — a foldable category with its rules — except every value sits in the
 * Monday column, since deviations are weekly rather than per-day. It honours the same tiles, filters
 * and view settings as the weeks grid; only these two categories are backed today.
 */
const BacklogWeekDetail = ({
  weekDays,
  weekField,
  backlog,
  ruleRows,
  filters,
  activeCategories,
  cellValueMode,
  collapsedCategoryKeys,
  onToggleCategoryRow,
  onClose
}: BacklogWeekDetailProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });
  const cellDisplay =
    cellValueMode === 'plannedOverPrescribed' ? 'ratio' : 'difference';
  const showOutsideTreatment = activeCategories.includes('outsideTreatment');

  const firstDay = weekDays[0];
  const lastDay = weekDays[weekDays.length - 1];
  const weekLabel = firstDay
    ? t('weekRange', {
        week: t('week'),
        weekNr: firstDay.weekNr,
        start: dayjs(firstDay.date).format('DD-MM'),
        end: dayjs(lastDay.date).format('DD-MM')
      })
    : '';

  const careReceivers = (backlog ?? [])
    .map((entry) => ({
      client: entry.planningEntity as ClientResponse,
      events: showOutsideTreatment
        ? selectWeekEvents(
            entry.outsideTreatmentEvents,
            weekDays,
            filters.activityCodes
          )
        : []
    }))
    .filter(
      ({ client }) =>
        !filters.careReceiverIds.length ||
        filters.careReceiverIds.includes(client.id)
    )
    .sort((a, b) =>
      (a.client.displayName ?? '').localeCompare(b.client.displayName ?? '')
    );

  return (
    <Container>
      <Breadcrumb>
        <IconButton size="small" onClick={onClose} aria-label={t('backToList')}>
          <Icon type="chevron-left" sx={{ width: 16 }} color="action" />
        </IconButton>
        <Typography variant="body1">{weekLabel}</Typography>
      </Breadcrumb>

      <Row>
        <LabelCell />
        {weekDays.map((day) => (
          <DayHeader key={day.date.toISOString()}>
            <Typography variant="body2">{day.dayName}</Typography>
            <Typography variant="caption">
              {dayjs(day.date).format('DD-MM')}
            </Typography>
          </DayHeader>
        ))}
      </Row>

      {careReceivers.map(({ client, events }) => {
        const deviationRules = ruleRows.filter(
          (row) =>
            row.category === 'deviation' &&
            row.careReceiverId === client.id &&
            asWeekValue(row[weekField]) !== null
        );

        // A care receiver with nothing to report this week is left out entirely.
        if (!deviationRules.length && !events.length) return null;

        const deviationCollapsed = collapsedCategoryKeys.has(
          getCategoryRowKey(client.id, 'deviation')
        );
        // The category's own value is the magnitude of its rules that week — the same figure the weeks
        // grid shows on the category row, here landing on Monday.
        const deviationOutstanding = deviationRules.reduce(
          (sum, row) =>
            sum + getWeekValueMagnitude(asWeekValue(row[weekField])),
          0
        );

        return (
          <div key={client.id}>
            <CareReceiverBand>
              {/* The planning-entity symbol — a care receiver is a client — the same one the detail
                  tooltip shows before the patients. */}
              <CareReceiverEntityIcon type={getIconType('CLIENT')} />
              {formatEntityDisplayLabel(
                client.displayName ?? '',
                client.number
              )}
            </CareReceiverBand>

            {deviationRules.length > 0 && (
              <>
                <Row>
                  <LabelCell>
                    <BacklogCategoryLabel
                      label={t('categories.deviation')}
                      count={deviationRules.length}
                      fold={{
                        isExpanded: !deviationCollapsed,
                        onToggle: () =>
                          onToggleCategoryRow(client.id, 'deviation'),
                        ariaLabel: t('toggleCategory', {
                          name: t('categories.deviation')
                        })
                      }}
                    />
                  </LabelCell>
                  {weekDays.map((day, dayIndex) => (
                    <DayCell key={day.date.toISOString()}>
                      {dayIndex === 0 && (
                        <WeekValueCell value={deviationOutstanding} isSummary />
                      )}
                    </DayCell>
                  ))}
                </Row>

                {!deviationCollapsed &&
                  deviationRules.map((row) => (
                    <Row key={row.id}>
                      <LabelCell indented>
                        <Typography variant="body2" noWrap>
                          {row.activityName}
                        </Typography>
                      </LabelCell>
                      {weekDays.map((day, dayIndex) => (
                        <DayCell key={day.date.toISOString()}>
                          {dayIndex === 0 && (
                            <WeekValueCell
                              value={asWeekValue(row[weekField])}
                              display={cellDisplay}
                            />
                          )}
                        </DayCell>
                      ))}
                    </Row>
                  ))}
              </>
            )}

            {events.length > 0 && (
              <Row>
                <LabelCell>
                  <BacklogCategoryLabel
                    label={t('categories.outsideTreatment')}
                    count={events.length}
                  />
                </LabelCell>
                {weekDays.map((day) => {
                  const dayEvents = events.filter((event) =>
                    dayjs(event.date).isSame(day.date, 'day')
                  );
                  return (
                    <DayCell key={day.date.toISOString()}>
                      {dayEvents.map((event, index) => (
                        <OutsideTreatmentAppointmentCard
                          key={`${event.id}-${index}`}
                          event={event}
                          careReceiverId={client.id}
                        />
                      ))}
                    </DayCell>
                  );
                })}
              </Row>
            )}
          </div>
        );
      })}
    </Container>
  );
};

export default BacklogWeekDetail;
